import { Button, Divider, Form, Input, Pagination, Skeleton, Spin } from "antd"
import { Button as CustomButton } from "components"
import plusIcon from "assets/svg/plus.svg"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { useState } from "react"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { useParams, useSearchParams } from "react-router-dom"
import { WorkplaceRes } from "./ViewWorkplace"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { IDutyLocationDoc } from "../typings"
import DutyLocationCard from "../components/DutyLocationCard"
import GeoPicker from "components/GeoPicker"
import { calculateRadius } from "../utils/shiftTablesUtils"
import { LocationFormValues } from "../setup/components/SetupDutyLocations"
import usePermissions from "hooks/usePermissions"
import { TimeAndAttendancePermission } from "constants/permissions"
import { useAppNotificationStore } from "stores"

type CreateDutyLocationReqBody = {
  name: string
  addressLine1: string
  addressLine2: string
  addressCity: string
  addressState: string
  stateCode: string
  zipCode: string
  countryCode: string
  country: string
  longitude: string
  latitude: string
}
const DutyLocationWorkplace = ({
  workplace,
  isLoading,
}: {
  workplace: WorkplaceRes | undefined
  isLoading: boolean
}) => {
  const [locationForm] = Form.useForm<LocationFormValues>()
  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()
  const [canManageDutyLocations] = usePermissions({
    allowedPermissions: [TimeAndAttendancePermission.CanManageDutyLocations],
  })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { x } = useParams()
  const [searchParams] = useSearchParams()
  const [page, setPage] = useState({
    currentPage: 1,
    pageSize: 10,
  })
  const [isEditing, setIsEditing] = useState(false)
  const [selectedDutyLocation, setSelectedDutyLocation] =
    useState<IDutyLocationDoc | null>()

  const dutyLocationIdRaw = searchParams.get("dutyLocationId")
  const dutyLocationId = dutyLocationIdRaw ? Number(dutyLocationIdRaw) : null
  const dutyLocationsToRender = !dutyLocationId
    ? workplace?.data?.officeDutyLocations ?? []
    : (workplace?.data?.officeDutyLocations ?? []).filter(
        (d) => !!d && String(d.id) === String(dutyLocationId)
      )

  const invalidateDutyLocations = () =>
    queryClient.invalidateQueries({
      queryKey: [`/v1/time-attendance/workplaces/${x}`],
    })

  const { mutate: createDutyLocation, isLoading: isCreateDutyLocationLoading } =
    useMutation({
      mutationFn: (body: CreateDutyLocationReqBody) => {
        return ewaAxiosInstance.post(
          `/v1/time-attendance/workplaces/${x}/duty-locations`,
          body
        )
      },
      onMutate: async (body) => {
        await queryClient.cancelQueries([`/v1/time-attendance/workplaces/${x}`])
        const previous = queryClient.getQueryData([
          `/v1/time-attendance/workplaces/${x}`,
        ])
        console.log("Previouis ->", previous)
        queryClient.setQueryData(
          [`/v1/time-attendance/workplaces/${x}`],
          (old: any) => {
            console.log("Old ->", old)
            if (!old) return old
            return {
              ...old,
              data: {
                officeDutyLocations: [
                  ...(old.officeDutyLocations || []),
                  {
                    ...body,
                    id: `temp-${Date.now()}`,
                    isActive: true,
                    isDeleted: false,
                    workplaceId: Number(x),
                  },
                ],
              },
            }
          }
        )
        return { previous }
      },
      onSuccess: () => {
        locationForm.resetFields()
        setIsModalOpen(false)
      },
      onError: (err, _body, context) => {
        if (context?.previous) {
          queryClient.setQueryData(
            [`/v1/time-attendance/workplaces/${x}`],
            context.previous
          )
        }
        onQueryError(err)
      },
      onSettled: () => {
        invalidateDutyLocations()
      },
    })

  const { mutate: updateDutyLocation, isLoading: isUpdatingLocation } =
    useMutation({
      mutationFn: async ({
        dutyLocationId,
        body,
      }: {
        dutyLocationId: number
        body: CreateDutyLocationReqBody
      }) => {
        const res = await ewaAxiosInstance.put(
          `/v1/time-attendance/workplaces/${x}/duty-locations/${dutyLocationId}`,
          body
        )
        return res.data
      },
      onMutate: async ({
        dutyLocationId,
        body,
      }: {
        dutyLocationId: number
        body: CreateDutyLocationReqBody
      }) => {
        await queryClient.cancelQueries([`/v1/time-attendance/workplaces/${x}`])
        const previous = queryClient.getQueryData([
          `/v1/time-attendance/workplaces/${x}`,
        ])
        queryClient.setQueryData(
          [`/v1/time-attendance/workplaces/${x}`],
          (old: any) => {
            if (!old) return old
            return {
              ...old,
              data: {
                officeDutyLocations: [
                  ...(old.officeDutyLocations || []),
                  {
                    id: dutyLocationId,
                    ...body,
                  },
                ],
              },
            }
          }
        )
        return { previous }
      },
      onError: (err, _body, context) => {
        if (context?.previous) {
          queryClient.setQueryData(
            [`/v1/time-attendance/workplaces/${x}`],
            context.previous
          )
        }
        onQueryError(err)
      },
      onSettled: () => {
        invalidateDutyLocations()
        locationForm.resetFields()
      },
      onSuccess: () => {
        setSelectedDutyLocation(null)
        locationForm.resetFields()
        setIsModalOpen(false)
        setIsEditing(false)
      },
    })

  const {
    mutate: deleteDutyLocation,
    isLoading: isDeletingDutyLocation,
    variables,
  } = useMutation({
    mutationFn: async (dutyLocationId: number) => {
      const res = await ewaAxiosInstance.delete(
        `/v1/time-attendance/workplaces/${x}/duty-locations/${dutyLocationId}`
      )
      return res.data
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries([`/v1/time-attendance/workplaces/${x}`])
      const previous = queryClient.getQueryData([
        `/v1/time-attendance/workplaces/${x}`,
      ])
      queryClient.setQueryData(
        [`/v1/time-attendance/workplaces/${x}`],
        (old: any) => {
          if (!old) return old
          return {
            ...old,
            data: {
              ...old.data,
              officeDutyLocations: old?.data?.officeDutyLocations?.filter(
                (dutyId: IDutyLocationDoc) => dutyId.id !== id
              ),
            },
          }
        }
      )
      return { previous }
    },
    onError: (err, _body, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          [`/v1/time-attendance/workplaces/${x}`],
          context.previous
        )
      }
      onQueryError(err)
    },
    onSettled: () => {
      invalidateDutyLocations()
    },
  })

  const handlePlaceDetails = (place: any) => {
    try {
      const comps = place.address_components ?? []

      const countryComp = comps.find((c: any) => c.types?.includes("country"))
      const stateComp = comps.find((c: any) =>
        c.types?.includes("administrative_area_level_1")
      )
      const cityComp =
        comps.find((c: any) => c.types?.includes("locality")) ||
        comps.find((c: any) => c.types?.includes("postal_town")) ||
        comps.find((c: any) => c.types?.includes("administrative_area_level_2"))
      const postalComp = comps.find((c: any) =>
        c.types?.includes("postal_code")
      )

      const formattedAddress =
        place.formatted_address ??
        locationForm.getFieldValue("locationAddress") ??
        ""

      locationForm.setFieldsValue({
        locationAddress: formattedAddress,
        countryCode: countryComp?.short_name ?? "",
        country: countryComp?.long_name ?? "",
        addressCity: cityComp?.long_name ?? cityComp?.short_name ?? "",
        addressState: stateComp?.long_name ?? stateComp?.short_name ?? "",
        stateCode: stateComp?.short_name ?? "",
        zipCode: postalComp?.long_name ?? postalComp?.short_name ?? "",
      })
    } catch {
      // ignore
    }
  }

  const handleOnFinish = (values: any) => {
    // Merge visible values with hidden geo values stored in locationForm
    const merged = {
      ...locationForm.getFieldsValue(true),
      ...values,
    }

    const body = {
      name: merged.locationName,
      addressLine1: merged.locationAddress,
      addressLine2: merged.addressLine2 ?? "",
      addressCity: merged.addressCity ?? "",
      addressState: merged.addressState ?? "",
      stateCode: merged.stateCode ?? "",
      zipCode: merged.zipCode ?? "",
      countryCode: merged.countryCode ?? "",
      country: merged.country ?? "",
      longitude: merged.longitude != null ? String(merged.longitude) : "",
      latitude: merged.latitude != null ? String(merged.latitude) : "",
    }

    if (isEditing && selectedDutyLocation) {
      updateDutyLocation({ dutyLocationId: selectedDutyLocation.id, body })
    } else {
      createDutyLocation(body)
    }
  }

  const dutyLocations = workplace?.data?.officeDutyLocations ?? []
  const isPrimaryFullWidth = (1 + dutyLocations.length) % 2 !== 0

  return (
    <div className="">
      <div className="flex justify-between items-cente mb-4">
        <h5 className="font-bold text-lg">Duty locations</h5>{" "}
        <Button
          className="border-2 border-gray-300 flex items-center gap-3 h-10"
          onClick={() => {
            if (!canManageDutyLocations) {
              toast.unauthorized()
              return
            }
            setIsEditing(false)
            setIsModalOpen(true)
          }}
        >
          <img
            src={plusIcon}
            width={15}
            height={15}
          />
          Create duty location
        </Button>
      </div>
      {!dutyLocationId && (
        <DutyLocationCard
          title="Primary"
          location={workplace?.data?.fullAddress || ""}
        />
      )}
      {isLoading ? (
        <div className="border-2 border-[#E2E8F0] rounded-md p-2 mt-2 ">
          <div className="flex items-center justify-between w-full mb-3">
            <Skeleton.Input
              active
              size="small"
              style={{ width: 20 }}
            />
            <Skeleton.Input
              active
              size="small"
              style={{ width: 20 }}
            />
          </div>

          <Skeleton
            active
            title={false}
            paragraph={{ rows: 1, width: "100%" }}
            style={{ width: "100%" }}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-2 mt-2">
          {dutyLocationsToRender.map((dutyLocation: any) => (
            <DutyLocationCard
              key={dutyLocation.id}
              title={dutyLocation?.name}
              location={dutyLocation?.addressLine1}
              onEdit={() => {
                if (!canManageDutyLocations) {
                  toast.unauthorized()
                  return
                }
                setSelectedDutyLocation(dutyLocation)
                setIsEditing(true)
                locationForm.setFieldsValue({
                  locationName: dutyLocation.name,
                  locationAddress: dutyLocation.addressLine1,
                })

                setIsModalOpen(true)
              }}
              onDelete={() => {
                if (!canManageDutyLocations) {
                  toast.unauthorized()
                  return
                }
                deleteDutyLocation(dutyLocation?.id)
              }}
              editIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-4 h-4 cursor-pointer hover:text-blue-500"
                  onClick={() => {
                    if (!canManageDutyLocations) {
                      toast.unauthorized()
                      return
                    }
                    setSelectedDutyLocation(dutyLocation)
                    setIsEditing(true)
                    locationForm.setFieldsValue({
                      locationName: dutyLocation.name,
                      locationAddress: dutyLocation.addressLine1,
                      addressCity: dutyLocation.addressCity,
                      addressState: dutyLocation.addressState,
                      stateCode: dutyLocation.stateCode,
                      zipCode: dutyLocation.zipCode,
                      countryCode: dutyLocation.countryCode,
                      country: dutyLocation.country,
                      longitude: dutyLocation.longitude
                        ? Number(dutyLocation.longitude)
                        : undefined,
                      latitude: dutyLocation.latitude
                        ? Number(dutyLocation.latitude)
                        : undefined,
                    })

                    setIsModalOpen(true)
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125"
                  />
                </svg>
              }
              deleteIcon={
                isDeletingDutyLocation && variables === dutyLocation?.id ? (
                  <Spin size="small" />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className={`w-4 h-4 cursor-pointer hover:text-red-500 ${
                      isDeletingDutyLocation
                        ? "pointer-events-none opacity-50"
                        : ""
                    }`}
                    onClick={() => {
                      if (!canManageDutyLocations) {
                        toast.unauthorized()
                        return
                      }
                      if (dutyLocation?.id != null) {
                        deleteDutyLocation(dutyLocation.id)
                      }
                    }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v .916m7 .5a48 .667 . . . -"
                    />
                  </svg>
                )
              }
            />
          ))}
        </div>
      )}
      {/* <div className="flex justify-start mt-6 mb-3">
        <Pagination
          current={page.currentPage}
          pageSize={page.pageSize}
          total={workplace?.data?.totalDocs || 0}
          showSizeChanger
          pageSizeOptions={[5, 10, 20, 50]}
          onChange={(page, pageSize) =>
            setPage({
              currentPage: page,
              pageSize: pageSize,
            })
          }
        />
      </div> */}

      <CustomModal
        rootClassName="ta-scope"
        width={600}
        open={isModalOpen}
        title={isEditing ? "Edit duty location" : "Create duty location"}
        onCancel={() => {
          setIsModalOpen(false)
          setIsEditing(false)
          setSelectedDutyLocation(null)
          locationForm.resetFields()
        }}
        footer={null}
      >
        <Form
          layout="vertical"
          form={locationForm}
          onFinish={handleOnFinish}
        >
          <Form.Item
            label={<p className="font-semibold">Location name</p>}
            name="locationName"
            rules={[{ required: true, message: "Please enter location name" }]}
          >
            <Input
              placeholder="Enter location address"
              style={{ width: "100%", height: "40px" }}
            />
          </Form.Item>
          <Form.Item
            label="Location address"
            name="locationAddress"
            rules={[
              { required: true, message: "Please enter location address" },
            ]}
          >
            <GeoPicker
              value={locationForm.getFieldValue("locationAddress")}
              onChange={(address: string) => {
                locationForm.setFieldsValue({ locationAddress: address })
              }}
              onSelect={(lat: number, lng: number) => {
                const radius = calculateRadius(lat, lng)
                locationForm.setFieldsValue({
                  latitude: lat,
                  longitude: lng,
                  radius,
                })
              }}
              onPlaceDetails={handlePlaceDetails}
            />
          </Form.Item>
          {/* <Form.Item
            label={<p className="font-semibold">Location address</p>}
            name="locationAddress"
            rules={[
              { required: true, message: "Please enter location address" },
            ]}
          >
            <Input
              placeholder="Enter workplace's address"
              style={{ width: "100%", height: "40px" }}
              prefix={
                <MagnifyingGlassIcon className="w-4 h-4 text-neutral-300" />
              }
            />
          </Form.Item> */}
          <Form.Item
            name="latitude"
            noStyle
          />
          <Form.Item
            name="longitude"
            noStyle
          />
          <Form.Item
            name="radius"
            noStyle
          />
          <Form.Item
            name="addressCity"
            noStyle
          />
          <Form.Item
            name="addressState"
            noStyle
          />
          <Form.Item
            name="stateCode"
            noStyle
          />
          <Form.Item
            name="zipCode"
            noStyle
          />
          <Form.Item
            name="countryCode"
            noStyle
          />
          <Form.Item
            name="country"
            noStyle
          />
          <Form.Item
            name="addressLine2"
            noStyle
          />

          <Form.Item>
            <CustomButton
              color="primary"
              style={{ width: "100%", height: "40px" }}
              className="whitespace-nowrap"
              submitting={isUpdatingLocation || isCreateDutyLocationLoading}
            >
              {isEditing ? "Update duty location" : "Create duty location"}
            </CustomButton>
          </Form.Item>
        </Form>
      </CustomModal>
    </div>
  )
}

export default DutyLocationWorkplace
